import { Dispatch, forwardRef, SetStateAction } from 'react';
import { Coverage } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { useTranslation } from 'react-i18next';
import { theme } from '@frontend/theme';
import { Button, Heading, useModalControl } from '@frontend/design-system';
import { DateRangeOptionValue } from '../utils/constants';
import { InsuranceDetails, UndefinedOrNull } from '../utils/types';
import { ActionsMenu } from './actions-menu';
import { ErrorBanner } from './error-banner';
import { InsuranceCardBannerNotification } from './insurance-card-banner-notification';
import { InsuranceDetailList } from './insurance-details-list';
import { VerifyInsuranceButton } from './insurance-status-chip-button';
import { VerificationMetricSidePanel } from './verification-metric-side-panel';
import { VerifiedInsuranceDetailsList } from './verified-insurance-details-list';
import { VerifyInsuranceSidePanel } from './verify-insurance-side-panel';

interface InsuranceProps {
  defaultDateRange: DateRangeOptionValue;
  locationId: string;
  personId: string;
  insuranceDetails: UndefinedOrNull<InsuranceDetails>;
  isDemoOrDevOffice: boolean;
  hasErrors: boolean;
  hasCoverage: boolean;
  hasActiveOrVerifiedInsurance: boolean;
  isInsuranceDetailsWritebackSupported: boolean;
  requestId?: string;
  isAutoVerification: boolean;
  personDetailsCoverage: UndefinedOrNull<Coverage>;
  isEnhancedInsuranceVerificationEnabled: boolean;
  setForceFetch: Dispatch<SetStateAction<boolean>>;
  updateDefaultDateRange: (range: DateRangeOptionValue) => void;
}
export const Insurance = forwardRef<HTMLElement, InsuranceProps>(
  (
    {
      locationId,
      personId,
      insuranceDetails,
      requestId,
      isDemoOrDevOffice,
      hasErrors,
      hasCoverage,
      hasActiveOrVerifiedInsurance,
      isInsuranceDetailsWritebackSupported,
      isAutoVerification,
      personDetailsCoverage,
      defaultDateRange,
      isEnhancedInsuranceVerificationEnabled,
      setForceFetch,
      updateDefaultDateRange,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const verifyInsuranceModalControl = useModalControl();
    const verificationMetricModalControl = useModalControl();
    //sync button and retrieving notification will only be shown in progress state for enhanced verification
    const enhancedVerificationInprogress =
      isEnhancedInsuranceVerificationEnabled && insuranceDetails?.verificationStatus === 'IN_PROGRESS';

    return (
      <>
        <section ref={ref} css={{ padding: theme.spacing(2, 2, 2) }}>
          <header css={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing(2) }}>
            <Heading level={3}>{t('Insurance')}</Heading>
            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
              {enhancedVerificationInprogress && (
                <Button
                  size='large'
                  iconName='sync'
                  variant='secondary'
                  onClick={() => {
                    setForceFetch(true);
                  }}
                />
              )}
              {(hasErrors || !hasCoverage) && !enhancedVerificationInprogress && (
                <VerifyInsuranceButton showWeaveVerify={true} onClick={verifyInsuranceModalControl.openModal} />
              )}
              <ActionsMenu
                verificationStatus={insuranceDetails?.thirdPartyCoverageStatus}
                onVerifyInsuranceClick={verifyInsuranceModalControl.openModal}
                onVerificationMetricClick={() => updateDefaultDateRange('today')}
                requestId={requestId}
              />
            </div>
          </header>
          <ErrorBanner
            isRetryButtonVisible //should be person data
            errorDetails={personDetailsCoverage?.thirdPartyCoverage?.errorDetails}
            onRetryClick={verifyInsuranceModalControl.openModal}
            isAutoVerification={isAutoVerification}
          />

          <InsuranceCardBannerNotification
            isEligibleForWebsiteResp={!!insuranceDetails?.isElegibleForWebsiteResp}
            isAutoVerification={isAutoVerification}
            isInsuranceDetailsWritebackSupported={isInsuranceDetailsWritebackSupported}
            isDemoOffice={isDemoOrDevOffice}
            enhancedVerificationInprogress={enhancedVerificationInprogress}
            hasCoverage={hasActiveOrVerifiedInsurance}
          />
          <>
            {hasActiveOrVerifiedInsurance ? (
              <VerifiedInsuranceDetailsList locationId={locationId || ''} personId={personId} />
            ) : (
              <InsuranceDetailList
                insuranceDetails={insuranceDetails as InsuranceDetails}
                locationId={locationId || ''}
                verifyInsuranceModalControl={verifyInsuranceModalControl}
                showInProgressStatus={enhancedVerificationInprogress}
                personId={personId}
              />
            )}
          </>
        </section>
        <VerifyInsuranceSidePanel
          {...verifyInsuranceModalControl.modalProps}
          onReportLearnMoreClick={() => updateDefaultDateRange('this-month')}
        />
        <VerificationMetricSidePanel {...verificationMetricModalControl.modalProps} dateRange={defaultDateRange} />
      </>
    );
  }
);
Insurance.displayName = 'Insurance';
